import { type ReactNode } from 'react';
import cx from 'clsx';
import { Button, type BaseButtonProps } from '@repo/design-system-kit';
import { useToast } from '@repo/design-system-kit';
import { useCopyToClipboard } from 'usehooks-ts';
import { type ButtonProps, Button as AriaButton } from 'react-aria-components';
import { IconCopyOutlined } from '@repo/monochrome-icons';
import styles from './copy-to-clipboard-button.module.css';

interface BaseCopyButtonProps extends ButtonProps {
  /* mode is used to determine the type of button to render */
  /* clipboard mode is used to render an unstyled variant of the component. Why? cause we have in the qonto-js app we have manually styled buttons in a lot of places */
  mode: 'clipboard' | 'button';
  className?: string;
  clipboardText: string;
  onError?: () => void;
  onSuccess?: () => void;
  toastLabel?: string;
  toastType?: 'info' | 'success';
  children?: ReactNode;
}

interface ClipboardTypeProps extends BaseCopyButtonProps {
  mode: 'clipboard';
  hasIcon?: boolean;
}

interface ButtonTypeProps extends BaseCopyButtonProps, BaseButtonProps {
  mode: 'button';
}

type CopyToClipboardButtonProps = ClipboardTypeProps | ButtonTypeProps;

export function CopyToClipboardButton(props: CopyToClipboardButtonProps): ReactNode {
  const {
    className,
    clipboardText,
    children,
    mode,
    onError,
    onSuccess,
    toastLabel,
    toastType = 'info',
    isDisabled,
  } = props;

  const { showToast } = useToast();
  const [_copiedText, copy] = useCopyToClipboard();

  const handlePress = async (): Promise<void> => {
    try {
      const result = await copy(clipboardText);
      // copy fn handles errors internally and returns false if it fails
      if (!result) {
        return onError?.();
      }
      if (toastLabel) {
        showToast({
          type: toastType,
          text: toastLabel,
        });
      }
      onSuccess?.();
    } catch (error) {
      onError?.();
    }
  };

  if (mode === 'clipboard') {
    return (
      <AriaButton
        className={cx('copy-btn', className)}
        data-clipboard-text={clipboardText}
        onPress={handlePress}
        {...props}
      >
        <span className={styles.clipboardModeContent}>{children}</span>
        {props.hasIcon ? <IconCopyOutlined className={styles.clipboardModeIcon} /> : null}
      </AriaButton>
    );
  }

  return (
    <Button
      className={cx('copy-btn', className)}
      data-clipboard-text={clipboardText}
      isDisabled={isDisabled}
      onPress={handlePress}
      {...props}
    >
      {children}
    </Button>
  );
}
